/**
 * Re-auth modal. Listens for the axios interceptor's REAUTH_EVENT and
 * presents a password prompt inline so the operator doesn't lose their
 * place. On success, the session blob is refreshed and the surrounding
 * page stays mounted.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";

import { REAUTH_EVENT, api, asApiError } from "../api/client";

import { useSession } from "./SessionContext";

export function ReauthModal() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function handle() {
      setOpen(true);
      setPassword("");
      setError(null);
    }
    window.addEventListener(REAUTH_EVENT, handle);
    return () => window.removeEventListener(REAUTH_EVENT, handle);
  }, []);

  if (!open || !user) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/auth/login", {
        username: user.gigafy_username,
        password,
      });
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      setOpen(false);
    } catch (err) {
      setError(asApiError(err).detail || "Re-auth failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-3 rounded-lg border border-amber-700 bg-slate-950 p-5 shadow-xl animate-enter"
      >
        <h2 className="text-lg font-semibold tracking-tight text-amber-200">
          Gigafy session expired
        </h2>
        <p className="text-sm text-slate-300">
          Re-enter your password to continue as <strong>{user.gigafy_username}</strong>.
        </p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          required
          autoComplete="current-password"
          className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
        />
        {error && (
          <div className="rounded border border-red-900 bg-red-950 px-3 py-2 text-xs text-red-200">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.location.assign("/login");
            }}
            className="rounded border border-slate-700 px-3 py-1 text-xs text-slate-200 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 active:scale-[0.96]"
          >
            Sign out
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 transition-[scale,background-color,color] duration-150 ease-out hover:bg-amber-400 active:enabled:scale-[0.96] disabled:opacity-50"
          >
            {submitting ? "Verifying…" : "Continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
